import { useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import SectionHeading from "@/components/base/SectionHeading";
import VideoCard from "@/components/feature/VideoCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryStrip from "@/pages/home/components/CategoryStrip";
import { categories, videos } from "@/mocks/videos";
import {
  formatDate,
  formatViews,
  getCategoryVideos,
  getFeaturedVideos,
  getLatestVideos,
  getTrendingVideos,
  sortVideos,
} from "@/lib/videos";
import { usePageMeta } from "@/hooks/usePageMeta";

const hotTabs = [{ id: "all", name: "全部" }, ...categories.map((item) => ({ id: item.id, name: item.name }))];

const stats = [
  { value: "20+", label: "精选视频", tone: "bg-primary-500 text-background-50" },
  { value: "6", label: "内容分类", tone: "bg-accent-100 text-accent-900" },
  { value: "100%", label: "免费观看", tone: "bg-secondary-100 text-secondary-900" },
  { value: "每日", label: "编辑更新", tone: "bg-background-100 text-foreground-900" },
];

export default function Home() {
  usePageMeta({
    title: "茄子视频-优质短视频分享与观看平台",
    description:
      "茄子视频提供丰富多样的短视频内容，涵盖娱乐、生活、搞笑等多个分类，用户可在线浏览和分享喜爱的视频，体验精彩视听盛宴。",
    keywords: "茄子视频,短视频,视频分享,娱乐视频,搞笑视频,生活视频,高清视频",
  });

  const [hotTab, setHotTab] = useState("all");
  const featured = getFeaturedVideos();
  const trending = getTrendingVideos(5);
  const fresh = getLatestVideos(3);
  const hotVideos = (
    hotTab === "all" ? sortVideos(videos, "latest") : getCategoryVideos(hotTab, "latest")
  ).slice(0, 8);

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1360px] px-4 pt-8 md:px-8 md:pt-10">
        <div className="flex flex-wrap items-end justify-between gap-5">
          <div>
            <p className="flex items-center gap-2 font-label text-[11px] font-semibold uppercase tracking-[0.2em] text-primary-600">
              <span className="h-1 w-6 rounded-full bg-accent-500"></span>
              优质短视频 · 每日更新
            </p>
            <h1 className="mt-2 font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-[34px]">
              茄子视频精选短视频
            </h1>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-foreground-500">
              娱乐、生活、搞笑、音乐、美食与旅行，精选内容一次刷个够。
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="flex items-center gap-1.5 rounded-full bg-background-100 px-3 py-1.5 text-foreground-600">
              <i className="ri-flashlight-line text-sm text-accent-600"></i>
              今日更新 {videos.length} 条
            </span>
            <span className="flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1.5 text-primary-700">
              <i className="ri-star-smile-line text-sm"></i>
              编辑精选 {featured.length} 条
            </span>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <HeroCarousel slides={featured} />
      </div>

      <section className="mx-auto mt-12 w-full max-w-[1360px] px-4 md:mt-16 md:px-8">
        <SectionHeading
          eyebrow="Browse by category"
          title="按分类逛逛"
          description="六大分类覆盖日常刷视频的多数场景，点进去就是一条完整的内容流。"
          action={
            <Link
              to="/category/entertainment"
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              查看全部分类
              <i className="ri-arrow-right-line text-sm"></i>
            </Link>
          }
        />
        <div className="mt-6">
          <CategoryStrip />
        </div>
      </section>

      <section className="mx-auto mt-14 w-full max-w-[1360px] px-4 md:mt-20 md:px-8">
        <SectionHeading
          eyebrow="Trending now"
          title="热门短视频"
          description="最近一周播放增长最快的作品，支持按分类快速切换。"
        />
        <div className="mt-5 flex flex-wrap items-center gap-2">
          {hotTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setHotTab(tab.id)}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-xs transition-colors ${
                hotTab === tab.id
                  ? "bg-primary-500 font-medium text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {tab.name}
            </button>
          ))}
        </div>
        <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 lg:grid-cols-4">
          {hotVideos.map((video) => (
            <VideoCard key={video.id} video={video} />
          ))}
        </div>
        <div className="mt-9 flex justify-center">
          <Link
            to={hotTab === "all" ? "/category/entertainment" : `/category/${hotTab}`}
            className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-primary-200 bg-primary-50 px-5 py-2.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100"
          >
            查看更多{hotTab === "all" ? "分类内容" : hotTabs.find((tab) => tab.id === hotTab)?.name + "视频"}
            <i className="ri-arrow-right-line text-sm"></i>
          </Link>
        </div>
      </section>

      <section className="mx-auto mt-14 w-full max-w-[1360px] px-4 md:mt-20 md:px-8">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <div className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
            <div className="flex items-end justify-between gap-4">
              <div>
                <p className="flex items-center gap-2 font-label text-[11px] font-semibold uppercase tracking-[0.2em] text-primary-600">
                  <span className="h-1 w-6 rounded-full bg-accent-500"></span>
                  Top charts
                </p>
                <h2 className="mt-2 font-heading text-lg font-extrabold text-foreground-950 md:text-xl">
                  本周热播榜
                </h2>
              </div>
              <Link
                to="/category/entertainment"
                className="whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-600"
              >
                更多榜单
              </Link>
            </div>
            <ol className="mt-4 divide-y divide-background-200/80">
              {trending.map((video, index) => (
                <li key={video.id}>
                  <Link to={`/video/${video.id}`} className="group flex items-center gap-3 py-3">
                    <span
                      className={`w-6 shrink-0 font-heading text-lg font-extrabold ${
                        index < 3 ? "text-accent-600" : "text-foreground-300"
                      }`}
                    >
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="line-clamp-1 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                        {video.title}
                      </p>
                      <p className="mt-1 flex flex-wrap items-center gap-x-2 text-[11px] text-foreground-500">
                        <span>{formatViews(video.views)} 播放</span>
                        <span className="text-foreground-300">·</span>
                        <span>{formatDate(video.uploadTime)}</span>
                      </p>
                    </div>
                    <div className="h-12 w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img
                        src={video.cover}
                        alt={video.title}
                        title={`${video.title} - 茄子视频热播榜`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                  </Link>
                </li>
              ))}
            </ol>
          </div>

          <div className="flex flex-col gap-5">
            <div className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <p className="flex items-center gap-2 font-label text-[11px] font-semibold uppercase tracking-[0.2em] text-primary-600">
                    <span className="h-1 w-6 rounded-full bg-accent-500"></span>
                    Fresh uploads
                  </p>
                  <h2 className="mt-2 font-heading text-lg font-extrabold text-foreground-950 md:text-xl">
                    新鲜上架
                  </h2>
                </div>
                <Link
                  to="/search?q=攻略"
                  className="whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-600"
                >
                  更多内容
                </Link>
              </div>
              <div className="mt-4 flex flex-col gap-3">
                {fresh.map((video) => (
                  <Link
                    key={video.id}
                    to={`/video/${video.id}`}
                    className="group flex items-center gap-3 rounded-lg bg-background-100/70 p-2.5 transition-colors hover:bg-primary-50"
                  >
                    <div className="relative h-16 w-28 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img
                        src={video.cover}
                        alt={video.title}
                        title={`${video.title} - 茄子视频最新上传`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="line-clamp-2 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-700">
                        {video.title}
                      </p>
                      <p className="mt-1 font-label text-[11px] text-foreground-500">
                        {formatDate(video.uploadTime)} · {formatViews(video.views)} 播放
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-xl bg-accent-100 p-5 md:p-6">
              <div className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-background-50/80 text-accent-700">
                  <i className="ri-heart-3-line text-xl"></i>
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-accent-950">
                    把喜欢的视频收进口袋
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-accent-900/80">
                    收藏功能保存在你的浏览器本地，不需要登录，刷新后依然记得你看过什么。
                  </p>
                  <Link
                    to="/favorites"
                    className="mt-3 inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-foreground-950 px-3.5 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-800"
                  >
                    打开我的收藏
                    <i className="ri-arrow-right-line text-sm"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto mt-14 w-full max-w-[1360px] px-4 md:mt-20 md:px-8">
        <div className="grid grid-cols-1 items-center gap-8 rounded-2xl bg-primary-50 p-6 md:p-10 lg:grid-cols-[1.15fr_0.85fr] lg:p-12">
          <div>
            <p className="flex items-center gap-2 font-label text-[11px] font-semibold uppercase tracking-[0.2em] text-primary-600">
              <span className="h-1 w-6 rounded-full bg-accent-500"></span>
              About Eggplant TV
            </p>
            <h2 className="mt-3 font-heading text-xl font-extrabold leading-snug text-foreground-950 md:text-3xl">
              一个不喧哗的短视频内容站
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-[15px]">
              茄子视频专注做三件事：把内容分好类、把封面和标题写清楚、把播放体验做干净。没有自动连播的噪音，也没有层层叠叠的弹窗，你只需要决定下一条看什么。
            </p>
            <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <li className="flex items-center gap-2 text-sm text-foreground-700">
                <i className="ri-checkbox-circle-fill text-base text-primary-500"></i>
                分类清晰
              </li>
              <li className="flex items-center gap-2 text-sm text-foreground-700">
                <i className="ri-checkbox-circle-fill text-base text-primary-500"></i>
                播放流畅
              </li>
              <li className="flex items-center gap-2 text-sm text-foreground-700">
                <i className="ri-checkbox-circle-fill text-base text-primary-500"></i>
                收藏本地保存
              </li>
            </ul>
            <Link
              to="/about"
              className="mt-7 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              了解茄子视频
              <i className="ri-arrow-right-line text-sm"></i>
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {stats.map((item) => (
              <div
                key={item.label}
                className={`flex min-h-[104px] flex-col justify-between rounded-xl p-4 md:p-5 ${item.tone}`}
              >
                <span className="font-heading text-2xl font-extrabold md:text-3xl">{item.value}</span>
                <span className="font-label text-[11px] uppercase tracking-[0.14em] opacity-80">
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}